import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO, { generateBreadcrumbSchema } from '@/components/SEO';
import { getCaseStudyBySlug, getCaseStudies } from '@/lib/api';
import { ArrowLeft, ArrowUpRight, Calendar, Tag, Loader2, Sparkles, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { SERVICES } from '@/lib/services';
import { trackCaseStudyView, trackCTA } from '@/lib/gtm';

const generateCaseStudySchema = (cs) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: cs.title,
  description: cs.excerpt,
  image: cs.cover_image,
  datePublished: cs.created_at,
  dateModified: cs.updated_at || cs.created_at,
  author: { '@type': 'Organization', name: 'Wizbang' },
  publisher: {
    '@type': 'Organization',
    name: 'Wizbang',
    logo: { '@type': 'ImageObject', url: 'https://www.wizbang.in/logo.png' },
  },
  mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.wizbang.in/work/${cs.slug}` },
  about: cs.client,
});

export default function WorkDetail() {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const load = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await getCaseStudyBySlug(slug);
      setCs(res.data);
      trackCaseStudyView(res.data.slug, res.data.client);
      try {
        const all = await getCaseStudies({ publishedOnly: true });
        setRelated((all.data || []).filter((x) => x.slug !== slug).slice(0, 3));
      } catch (_) {}
    } catch (err) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: '#FAFAF7' }}>
        <Navbar />
        <div className="pt-40 pb-24 flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 text-[#ff5a1f] animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !cs) return <Navigate to="/work" replace />;

  return (
    <div className="min-h-screen" style={{ background: '#FAFAF7' }} data-testid={`case-study-${cs.slug}`}>
      <SEO
        title={`${cs.title} — ${cs.client} Case Study`}
        description={cs.excerpt}
        keywords={(cs.tags || []).join(', ')}
        url={`/work/${cs.slug}`}
        image={cs.cover_image}
        type="article"
        article={{
          publishedTime: cs.created_at,
          modifiedTime: cs.updated_at || cs.created_at,
          author: 'Wizbang',
          tags: cs.tags,
        }}
        structuredData={{
          '@context': 'https://schema.org',
          '@graph': [
            generateCaseStudySchema(cs),
            generateBreadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Work', path: '/work' },
              { name: cs.title, path: `/work/${cs.slug}` },
            ]),
          ],
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-12 relative">
        <div className="absolute inset-0 bg-gradient-radial opacity-50" />
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
          <Link to="/work" className="inline-flex items-center gap-2 text-[#5B5B5B] hover:text-[#ff5a1f] text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> All work
          </Link>

          <div className="flex flex-wrap gap-2 mb-6">
            {(cs.services || []).map((slug2) => {
              const s = SERVICES.find((x) => x.slug === slug2);
              return s ? (
                <Link key={slug2} to={`/services/${s.slug}`}
                  className="px-3 py-1 bg-white border text-xs font-medium text-[#0A0A0A] rounded-full hover:border-[#ff5a1f] transition-colors"
                  style={{ borderColor: 'var(--border)' }}>
                  {s.title}
                </Link>
              ) : null;
            })}
          </div>

          <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-4">
            {cs.client}{cs.industry ? ` · ${cs.industry}` : ''}{cs.year ? ` · ${cs.year}` : ''}
          </div>
          <h1 className="font-syne text-4xl sm:text-5xl lg:text-7xl font-bold text-[#0A0A0A] mb-6 tracking-tight leading-[1.0]">
            {cs.title}
          </h1>
          <p className="text-[#3A3A3A] text-lg md:text-xl max-w-3xl leading-relaxed">{cs.excerpt}</p>
        </div>
      </section>

      {/* Metrics band */}
      {cs.metrics?.length > 0 && (
        <section className="dark-section py-16" data-testid="case-study-metrics">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="font-mono text-xs uppercase tracking-widest text-[#ff5a1f] mb-6">// Outcomes</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {cs.metrics.map((m, i) => (
                <div key={i} className="border-l-2 border-[#ff5a1f] pl-6">
                  <p className="font-syne text-5xl md:text-6xl font-bold text-white tracking-tighter">{m.value}</p>
                  <p className="text-white/60 text-sm uppercase tracking-wider mt-3">{m.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Story */}
      <section className="py-20 bg-white" data-testid="case-study-body">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="grid gap-12">
            {cs.challenge && (
              <div>
                <h2 className="font-syne text-2xl font-bold text-[#0A0A0A] mb-4 tracking-tight">Challenge</h2>
                <p className="text-[#3A3A3A] leading-relaxed whitespace-pre-line">{cs.challenge}</p>
              </div>
            )}
            {cs.solution && (
              <div>
                <h2 className="font-syne text-2xl font-bold text-[#0A0A0A] mb-4 tracking-tight">Solution</h2>
                <p className="text-[#3A3A3A] leading-relaxed whitespace-pre-line">{cs.solution}</p>
              </div>
            )}
            {cs.results && (
              <div>
                <h2 className="font-syne text-2xl font-bold text-[#0A0A0A] mb-4 tracking-tight">Results</h2>
                <p className="text-[#3A3A3A] leading-relaxed whitespace-pre-line">{cs.results}</p>
              </div>
            )}
            {cs.content && (
              <div className="prose prose-lg max-w-none">
                <div dangerouslySetInnerHTML={{ __html: cs.content.replace(/\n/g, '<br/>') }} />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Tags / duration */}
      <section className="py-12 bg-[#FAFAF7] border-y" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex flex-wrap items-center gap-6 text-sm">
          {cs.duration && (
            <span className="flex items-center gap-2 text-[#5B5B5B]">
              <Calendar className="w-4 h-4 text-[#ff5a1f]" />Duration: {cs.duration}
            </span>
          )}
          {cs.tags?.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-[#ff5a1f]" />
              {cs.tags.map((t) => (
                <span key={t} className="px-3 py-1 bg-white border rounded-full text-xs text-[#0A0A0A]" style={{ borderColor: 'var(--border)' }}>{t}</span>
              ))}
            </div>
          )}
          {cs.created_at && (
            <span className="text-[#8A8A8A] ml-auto">Published {format(new Date(cs.created_at), 'MMM d, yyyy')}</span>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-syne text-4xl md:text-5xl font-bold text-[#0A0A0A] mb-6 tracking-tight">
            Want results like <span className="font-editorial text-[#ff5a1f]">{cs.client}?</span>
          </h2>
          <p className="text-[#5B5B5B] text-lg mb-10">Tell us your numbers. We'll come back with a punchy 1-page proposal in 48 hours.</p>
          <Link to="/book-appointment" onClick={() => trackCTA('case_study_bottom_cta', `work_${cs.slug}`)}
            className="bg-[#0A0A0A] text-white px-10 py-5 text-sm font-semibold rounded-full hover:bg-[#ff5a1f] transition-colors btn-press inline-flex items-center gap-2">
            Book a call <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 bg-[#FAFAF7] border-t" style={{ borderColor: 'var(--border)' }}>
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-10">
              <h3 className="font-syne text-2xl md:text-3xl font-bold text-[#0A0A0A] tracking-tight">More work</h3>
              <Link to="/work" className="text-[#0A0A0A] font-semibold text-sm hover:text-[#ff5a1f] inline-flex items-center gap-2">
                View all <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.id} to={`/work/${r.slug}`} className="group block bg-white border rounded-2xl p-6 hover:border-[#ff5a1f] transition-colors"
                  style={{ borderColor: 'var(--border)' }}>
                  <p className="font-mono text-xs uppercase tracking-wider text-[#8A8A8A] mb-3">{r.client}{r.industry ? ` · ${r.industry}` : ''}</p>
                  <h4 className="font-syne text-lg font-bold text-[#0A0A0A] tracking-tight group-hover:text-[#ff5a1f] transition-colors line-clamp-2 mb-4">{r.title}</h4>
                  <div className="inline-flex items-center gap-1 text-[#0A0A0A] font-semibold text-sm group-hover:text-[#ff5a1f]">
                    Read case study <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
